import { CheckCircle2, ChevronRight } from "../../../../components/icons";
import { Match } from "../../types";
import { PeriodPhase, PHASE_RANK } from "../../hooks/usePeriodManager";

interface MatchPeriodSelectorProps {
  match: Match | null;
  operatorPeriod: number;
  currentPhase: PeriodPhase;
  isExtraTime: boolean;
  extraTimeSeconds: number;
  globalClock: string;
  isClockRunning: boolean;
  onTransitionToHalftime?: () => void;
  onTransitionToSecondHalf?: () => void;
  onTransitionToFulltime?: () => void;
  onTransitionToExtraFirst?: () => void;
  onTransitionToExtraHalftime?: () => void;
  onTransitionToExtraSecond?: () => void;
  onTransitionToPenalties?: () => void;
  onFinishMatch?: () => void;
  transitionDisabled?: boolean;
  /**
   * PMA-COCK-200 (UC2): disables only the Penalties controls (both entry
   * points — `btn-start-penalties-fulltime` and `btn-start-penalties`).
   * Deliberately separate from `transitionDisabled`, which still gates the
   * period-end controls (`btn-end-match-final`, `btn-start-extra-time`,
   * `btn-end-match-extra`) unchanged. Defaults to `transitionDisabled` when
   * omitted so every existing caller (tests in particular) keeps its prior
   * behavior.
   */
  penaltiesTransitionDisabled?: boolean;
  transitionReason?: string;
  t: any;
}

/**
 * PMA-COCK-210 (EPIC-61 brief 3, client wave 2026-09-19). Was a horizontal
 * status badge with a single transition button whose label/colour/testid
 * all changed with the phase (green/yellow/blue/orange/rose/slate for one
 * concept) plus a separate 1H/2H/ET/PEN dot timeline. Locked decision 2:
 * "today an absent control is indistinguishable from a wrong one" — the
 * real cost that produced the missing "Penales at 120'" report. Replaced by
 * a vertical ladder: every phase row always drawn, exactly one live,
 * completed rows dimmed, unreachable rows visible and greyed.
 *
 * Every `data-testid` this component rendered before this change is
 * preserved unchanged — `period-status-*` (12 e2e specs assert only
 * `toBeVisible()`/`toHaveCount(0)` on these, never their text, confirmed by
 * grep), `transition-reason` (3 e2e specs), and every `btn-*` transition
 * testid (`MatchPeriodSelector.test.tsx`,
 * `MatchPeriodSelector.penaltiesEntry.integration.test.tsx`, and a further
 * dozen e2e specs). Only the visual structure and the old 1H/2H/ET/PEN
 * timeline (no testid, not asserted anywhere — grep confirmed) changed.
 *
 * Design decision (corrected after lead review — the spec's own six-row
 * ASCII wireframe is a sketch, not a settled description of the state
 * machine, and was rejected as the basis for this question): FULLTIME
 * (after regulation, before extra time/penalties/finish) and EXTRA_HALFTIME
 * (between the two extra-time halves) each get their OWN dedicated ladder
 * row — eight rows total, not six — rather than leaving the ladder with
 * zero live rows while resting in either. The alternative considered was
 * marking "T. extra 2" live-with-a-break-indicator while sitting in
 * EXTRA_HALFTIME; a dedicated row was chosen instead because it keeps
 * "exactly one row is live" true without exception (previously: "the LIVE
 * marker is about a period actually in progress; a resting decision point
 * has none" — a rule this fix retires by giving every resting point its
 * own row instead of carving out an exception to it) and because it makes
 * `period-status-fulltime`/`period-status-extra-halftime` render through
 * the SAME generic live-row mechanism every other phase already uses,
 * instead of the separate `gapStatusTestId`/aria-label special case this
 * replaces (kept, narrowed, only for COMPLETED reusing "fulltime" below —
 * COMPLETED itself intentionally shows zero live rows, a terminal state).
 * FULLTIME's row carries no action button of its own — the three-way fork
 * it rests at ("T. extra 1" start, "Penales" start, "Terminar partido")
 * stays distributed across those rows/the bottom button, unchanged.
 * EXTRA_HALFTIME's row carries its own "Iniciar T. extra 2" action,
 * mirroring how the HALFTIME row carries its own "Iniciar 2do tiempo" —
 * this moves off SECOND_HALF_EXTRA_TIME's row, where it lived only because
 * EXTRA_HALFTIME previously had nowhere of its own to put it. SECOND_
 * HALF_EXTRA_TIME (live) remains its own fork: "Penales" (start) and
 * "Terminar partido" both become actionable off the live row itself.
 *
 * **Addendum (EPIC-73 brief 7/8/9, client report 2026-09-20,
 * `specs/2026-09-20-shot-placement-unreachable-and-phase-ladder-compaction-design.md`,
 * original text above kept unedited).** The client's own follow-up report:
 * eight always-drawn rows plus a full-width precondition notice plus a
 * separate finish button "needs to be more ux and more compact" — seven of
 * the eight rows are inert most of a match. Client decision, 2026-09-20:
 * show the sequence up to FULLTIME ("Fin del tiempo reglamentario")
 * normally, and reveal the four extra-time/penalties rows only once the
 * match can actually reach them. This narrows this UC's own "every row
 * always drawn regardless of phase" guarantee (design decision 2 above) for
 * those four rows specifically — see `EXTRA_PHASES`/`extraPhasesReachable`
 * below for the mechanism, and `cockpit-contract.md` PMA-COCK-216 for the
 * full record. Every other guarantee this UC established is unchanged: a
 * VISIBLE row that is merely blocked (not yet reachable within the
 * currently-open fork, e.g. Penales while `penaltiesTransitionDisabled`)
 * still renders disabled, never absent — locked decision 2's actual
 * concern, an admin/guard block versus a genuinely unreachable phase, are
 * two different things, and this addendum only ever touches the second.
 */
type LadderPhase =
  | "FIRST_HALF"
  | "HALFTIME"
  | "SECOND_HALF"
  | "FULLTIME"
  | "FIRST_HALF_EXTRA_TIME"
  | "EXTRA_HALFTIME"
  | "SECOND_HALF_EXTRA_TIME"
  | "PENALTIES";

type RowVisualState = "completed" | "live" | "future";

interface LadderRowDef {
  phase: LadderPhase;
  statusTestId: string;
  labelKey: string;
  labelFallback: string;
}

// Row order and labels are locked decision 4 — the client's own vernacular,
// in full, not abbreviations. `statusTestId` values are the pre-existing
// `period-status-*` suffixes (unchanged from the old switch statement).
const LADDER_ROWS: LadderRowDef[] = [
  {
    phase: "FIRST_HALF",
    statusTestId: "first-half",
    labelKey: "logger.ladder.firstHalf",
    labelFallback: "1er tiempo",
  },
  {
    phase: "HALFTIME",
    statusTestId: "halftime",
    labelKey: "logger.ladder.halftime",
    labelFallback: "Descanso",
  },
  {
    phase: "SECOND_HALF",
    statusTestId: "second-half",
    labelKey: "logger.ladder.secondHalf",
    labelFallback: "2do tiempo",
  },
  {
    // Own row (see this file's header comment) — was a `gapStatusTestId`
    // special case. Reuses the pre-existing "fulltime" testid suffix and
    // `logger.period.regulationEnded` label, both already in use before
    // this change (no new i18n key needed for this row).
    phase: "FULLTIME",
    statusTestId: "fulltime",
    labelKey: "logger.period.regulationEnded",
    labelFallback: "Fin del tiempo reglamentario",
  },
  {
    phase: "FIRST_HALF_EXTRA_TIME",
    statusTestId: "extra-first",
    labelKey: "logger.ladder.extraFirst",
    labelFallback: "T. extra 1",
  },
  {
    // Own row (see this file's header comment) — was a `gapStatusTestId`
    // special case. Label is CONFIRMED — "Descanso T. extra" is the
    // client's own word for this break, confirmed 2026-09-20 at EPIC-61's
    // consent screen (see cockpit-contract.md PMA-COCK-210).
    phase: "EXTRA_HALFTIME",
    statusTestId: "extra-halftime",
    labelKey: "logger.ladder.extraHalftime",
    labelFallback: "Descanso T. extra",
  },
  {
    phase: "SECOND_HALF_EXTRA_TIME",
    statusTestId: "extra-second",
    labelKey: "logger.ladder.extraSecond",
    labelFallback: "T. extra 2",
  },
  {
    phase: "PENALTIES",
    statusTestId: "penalties",
    labelKey: "logger.ladder.penalties",
    labelFallback: "Penales",
  },
];

// PMA-COCK-216 (EPIC-73 brief 9, client wave 2026-09-20). These four phases
// are unreachable from any regulation-time state — the match can only ever
// reach them by first passing through FULLTIME's own three-way fork (start
// extra time / start penalties / finish). Hiding them until
// `PHASE_RANK[currentPhase] >= PHASE_RANK.FULLTIME` removes 4 of 8 rows for
// every match that never sees extra time or penalties — the overwhelming
// majority. The reveal condition is `currentPhase`'s own rank, the same
// value that already drives every row's completed/live/future state above
// — never a UI toggle or a collapsed-by-default control an operator has to
// find. That is deliberate: a UI-state reveal can disagree with what the
// match can actually do (the exact shape of the 120-minute "Finalizar
// tiempo extra" defect this workspace already shipped once), a match-state
// reveal cannot, by construction — the row appears the instant the fork
// that leads to it opens, on the same render as every other consequence of
// reaching FULLTIME.
const EXTRA_PHASES = new Set<LadderPhase>([
  "FIRST_HALF_EXTRA_TIME",
  "EXTRA_HALFTIME",
  "SECOND_HALF_EXTRA_TIME",
  "PENALTIES",
]);

function rowVisualState(
  rowPhase: LadderPhase,
  currentPhase: PeriodPhase,
): RowVisualState {
  const rowRank = PHASE_RANK[rowPhase];
  const currentRank = PHASE_RANK[currentPhase];
  if (currentRank > rowRank) return "completed";
  if (currentRank === rowRank) return "live";
  return "future";
}

function formatExtraClock(extraTimeSeconds: number): string {
  if (extraTimeSeconds <= 0) return "";
  const mins = Math.floor(extraTimeSeconds / 60);
  const secs = String(extraTimeSeconds % 60).padStart(2, "0");
  return `+${mins}:${secs}`;
}

// Shared row-action button styling — sky is the one colour locked decision
// 1 assigns to "advance" (every phase-progression control), regardless of
// which phase or which of a fork's several simultaneously-reachable
// options it is. `min-h-11` matches the rail's other touch targets
// (PMA-COCK-207). `text-slate-400` (not -500, 3.07:1 on dark cards has
// shipped as a defect twice) for anything muted.
const ADVANCE_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-1 min-h-11 px-3 rounded-md border border-sky-500/40 bg-sky-500/15 text-sky-200 text-xs font-semibold uppercase tracking-wide transition-colors hover:bg-sky-500/25 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-sky-500/15";

export function MatchPeriodSelector({
  match,
  currentPhase,
  extraTimeSeconds,
  onTransitionToHalftime,
  onTransitionToSecondHalf,
  onTransitionToFulltime,
  onTransitionToExtraFirst,
  onTransitionToExtraHalftime,
  onTransitionToExtraSecond,
  onTransitionToPenalties,
  onFinishMatch,
  transitionDisabled = false,
  penaltiesTransitionDisabled = transitionDisabled,
  transitionReason,
  t,
}: MatchPeriodSelectorProps) {
  if (!match) return null;

  // The bottom "Terminar partido" button's reachability + testid depend on
  // which phase the match is currently in — three distinct entry points on
  // the pre-existing state machine (FULLTIME/SECOND_HALF_EXTRA_TIME/
  // PENALTIES), each its own testid, unchanged from the old code.
  const finishMatchReachable =
    currentPhase === "FULLTIME" ||
    currentPhase === "SECOND_HALF_EXTRA_TIME" ||
    currentPhase === "PENALTIES";
  const finishMatchTestId =
    currentPhase === "FULLTIME"
      ? "btn-end-match-final"
      : currentPhase === "SECOND_HALF_EXTRA_TIME"
        ? "btn-end-match-extra"
        : currentPhase === "PENALTIES"
          ? "btn-end-penalties"
          : // Not yet reachable from any of the three phases above — a
            // generic, new testid rather than falsely claiming one of the
            // three phase-specific ones is present while disabled.
            "btn-finish-match";

  const extraClock = formatExtraClock(extraTimeSeconds);

  // PMA-COCK-216 (EPIC-73 brief 9): the four extra-time/penalties rows stay
  // hidden until the match can actually reach them. See `EXTRA_PHASES`'s own
  // comment above for why this is match-state-driven, not a UI toggle.
  const extraPhasesReachable = PHASE_RANK[currentPhase] >= PHASE_RANK.FULLTIME;
  const visibleRows = LADDER_ROWS.filter(
    (row) => extraPhasesReachable || !EXTRA_PHASES.has(row.phase),
  );

  return (
    <div className="flex flex-col gap-1" data-testid="phase-ladder">
      {currentPhase === "COMPLETED" && (
        // Narrow special case, kept from the old `gapStatusTestId` logic:
        // FULLTIME's row (below) reuses the "fulltime" testid suffix, but
        // that row shows "completed" — not "live" — once the match reaches
        // COMPLETED (rank 9, past FULLTIME's rank 4), so the generic
        // live-row testid mechanism below no longer renders it. COMPLETED
        // reusing "fulltime" is the pre-existing contract (matches the old
        // code exactly, `status.stage === "fulltime-options" ||
        // "completed" ? "fulltime" : ...`) — preserved here, not a new
        // decision. COMPLETED itself intentionally shows zero LIVE rows
        // (a terminal state, nothing in progress); this sr-only span exists
        // only so the testid still resolves, aria-label not a text child
        // to avoid colliding with the FULLTIME row's own visible label.
        <span
          data-testid="period-status-fulltime"
          className="sr-only"
          aria-label={t("logger.period.regulationEnded", "Regulation Ended")}
        />
      )}

      {visibleRows.map((row) => {
        const state = rowVisualState(row.phase, currentPhase);
        const label = t(row.labelKey, row.labelFallback);
        // PMA-COCK-210 NOT_STARTED addendum (EPIC-61, lead product decision,
        // 2026-09-19, T15). NOT_STARTED (rank 0) has no ladder row of its
        // own — `rowVisualState` above returns "future" for every row, since
        // none shares its rank — so a not-yet-started match previously
        // showed EIGHT identical grey rows with nothing marking what
        // happens next, contradicting this file's own locked decision 2
        // ("an absent control is indistinguishable from a wrong one") and
        // the very defect this ladder exists to prevent. This is an
        // INDICATOR ONLY: no action button renders here regardless (see
        // `action` below, untouched) — `btn-start-clock`
        // ("Iniciar", `MatchTimerDisplay.tsx`) remains the SOLE kickoff
        // control. Two live controls for one transition is the exact shape
        // that made PMA-COCK-209 (the ExtraTimeAlert silently routing the
        // operator away from Penalties) possible; this row must never grow
        // one.
        const isUpNext =
          currentPhase === "NOT_STARTED" && row.phase === "FIRST_HALF";

        // Which row (if any) carries an actionable "advance" button right
        // now, and with which testid/handler/disabled input. Most rows
        // carry their OWN forward action while live; SECOND_HALF_EXTRA_TIME
        // is a fork (its own live row carries no action — Penales + the
        // bottom button do); FIRST_HALF_EXTRA_TIME's forward action also
        // lands early on ITSELF while resting at FULLTIME (fork branch 1 of
        // 3, see below); PENALTIES' own live row carries no action (the
        // bottom button finishes it).
        let action: {
          testId: string;
          onClick?: () => void;
          disabled: boolean;
          labelKey: string;
          labelFallback: string;
        } | null = null;

        if (row.phase === "FIRST_HALF" && state === "live") {
          action = {
            testId: "btn-end-first-half",
            onClick: onTransitionToHalftime,
            disabled: transitionDisabled,
            labelKey: "logger.ladder.endFirstHalf",
            labelFallback: "Terminar 1er tiempo",
          };
        } else if (row.phase === "HALFTIME" && state === "live") {
          action = {
            testId: "btn-start-second-half",
            onClick: onTransitionToSecondHalf,
            disabled: transitionDisabled,
            labelKey: "logger.ladder.startSecondHalf",
            labelFallback: "Iniciar 2do tiempo",
          };
        } else if (row.phase === "SECOND_HALF" && state === "live") {
          action = {
            testId: "btn-end-match",
            onClick: onTransitionToFulltime,
            disabled: transitionDisabled,
            labelKey: "logger.ladder.endSecondHalf",
            labelFallback: "Terminar 2do tiempo",
          };
        } else if (row.phase === "FIRST_HALF_EXTRA_TIME") {
          if (state === "live") {
            action = {
              testId: "btn-end-extra-first",
              onClick: onTransitionToExtraHalftime,
              disabled: transitionDisabled,
              labelKey: "logger.ladder.endExtraFirst",
              labelFallback: "Terminar T. extra 1",
            };
          } else if (currentPhase === "FULLTIME") {
            // Fork branch 1 of 3 off the FULLTIME gap phase.
            action = {
              testId: "btn-start-extra-time",
              onClick: onTransitionToExtraFirst,
              disabled: transitionDisabled,
              labelKey: "logger.ladder.startExtraFirst",
              labelFallback: "Iniciar T. extra 1",
            };
          }
        } else if (row.phase === "EXTRA_HALFTIME" && state === "live") {
          // EXTRA_HALFTIME's own row now carries this action directly
          // (mirrors HALFTIME's "Iniciar 2do tiempo" pattern) — previously
          // lived on SECOND_HALF_EXTRA_TIME's row only because EXTRA_
          // HALFTIME had no row of its own to put it on.
          action = {
            testId: "btn-start-extra-second",
            onClick: onTransitionToExtraSecond,
            disabled: transitionDisabled,
            labelKey: "logger.ladder.startExtraSecond",
            labelFallback: "Iniciar T. extra 2",
          };
        } else if (
          row.phase === "PENALTIES" &&
          (currentPhase === "FULLTIME" ||
            currentPhase === "SECOND_HALF_EXTRA_TIME")
        ) {
          // Fork branch reachable from either FULLTIME (direct route,
          // PMA-COCK-199) or SECOND_HALF_EXTRA_TIME (PMA-COCK-200) — same
          // handler, two separately-assertable testids, exactly as before.
          action = {
            testId:
              currentPhase === "FULLTIME"
                ? "btn-start-penalties-fulltime"
                : "btn-start-penalties",
            onClick: onTransitionToPenalties,
            disabled: penaltiesTransitionDisabled,
            labelKey: "logger.ladder.startPenalties",
            labelFallback: "Iniciar Penales",
          };
        }

        // Locked decision 6: text-slate-500 on these dark cards is 3.07:1
        // and has shipped as a WCAG AA contrast defect twice already —
        // slate-400 is the floor for every muted state here, never -500.
        // isUpNext gets its own sky treatment (locked decision 1's "advance"
        // colour) — visually distinct from both live (emerald) and future
        // (slate), since it is neither: the period has not started, but it
        // is the one true next step, not just another greyed-out row.
        const rowTextClass = isUpNext
          ? "text-sky-300 font-semibold"
          : state === "live"
            ? "text-emerald-300 font-bold"
            : state === "completed"
              ? "text-slate-400"
              : action
                ? "text-slate-300"
                : "text-slate-400";

        return (
          <div
            key={row.phase}
            className="flex flex-col gap-1"
            data-testid={
              state === "live"
                ? `period-status-${row.statusTestId}`
                : isUpNext
                  ? "ladder-up-next"
                  : undefined
            }
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="shrink-0 w-4 flex items-center justify-center">
                {state === "completed" ? (
                  <CheckCircle2 size={14} className="text-emerald-500" />
                ) : state === "live" ? (
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                  </span>
                ) : isUpNext ? (
                  <span className="inline-flex h-2.5 w-2.5 rounded-full border-2 border-sky-400" />
                ) : (
                  <span className="inline-flex h-1.5 w-1.5 rounded-full bg-slate-600" />
                )}
              </span>
              <span className={`text-sm truncate ${rowTextClass}`}>
                {label}
              </span>
              {state === "live" && (
                <span
                  data-testid="ladder-live-badge"
                  className="text-[10px] font-bold uppercase tracking-wider text-emerald-400"
                >
                  {t("logger.ladder.live", "LIVE")}
                </span>
              )}
              {state === "live" && extraClock && (
                <span className="text-[10px] font-mono font-bold text-yellow-500">
                  {extraClock}
                </span>
              )}
              {isUpNext && (
                <span
                  data-testid="ladder-up-next-badge"
                  className="text-[10px] font-bold uppercase tracking-wider text-sky-400"
                >
                  {t("logger.ladder.upNext", "PRÓXIMO")}
                </span>
              )}
            </div>
            {action && (
              <button
                type="button"
                data-testid={action.testId}
                onClick={action.onClick}
                disabled={action.disabled}
                className={`${ADVANCE_BUTTON_CLASS} ml-6 self-start`}
              >
                <span>{t(action.labelKey, action.labelFallback)}</span>
                <ChevronRight size={12} />
              </button>
            )}
            {/* PMA-COCK-210 NOT_STARTED addendum: no button here, by design
                (see isUpNext's own comment above) — names the next step and
                points at the real control instead. Mirrors the visible-
                reason idiom already established by the ladder's own
                `transition-reason` and `MatchTimerDisplay.tsx`'s
                `clock-start-wrong-phase-reason` (PMA-COCK-213), just running
                in the opposite direction: those point FROM Iniciar TO this
                panel; this points FROM this panel TO Iniciar. */}
            {isUpNext && (
              <div
                data-testid="ladder-up-next-reason"
                className="text-[11px] leading-snug text-sky-200 bg-sky-500/10 border border-sky-500/30 rounded px-2 py-1 ml-6"
              >
                {t(
                  "logger.ladder.upNextReason",
                  'Saque inicial: usa "Iniciar" en el panel de reloj.',
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* PMA-COCK-216 (EPIC-73 brief 10): unmounted entirely, not merely
          hidden, whenever `transitionReason` is falsy — zero DOM footprint,
          so it never holds real estate for a phase it does not block. Only
          ever truthy for the phase whose own advance it is actually
          blocking (see `useCockpitTransitionState.ts`'s `transitionReason`
          derivation, phase-scoped by construction) — this was already true
          before this wave; kept unchanged, pinned by a new regression test
          rather than left to erode silently during the density pass. */}
      {transitionReason && (
        <div
          className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2.5 py-1 ml-6"
          data-testid="transition-reason"
        >
          {transitionReason}
        </div>
      )}

      <div className="border-t border-slate-700/60 pt-1 mt-0">
        {/* Locked decision 3: outlined, never filled — the one control
            that must not become a mis-tap next to "Iniciar 2do tiempo"/
            the extra-time start actions above it. Always drawn (design
            decision 2), disabled + non-interactive until one of its three
            reachable phases is current. */}
        <button
          type="button"
          data-testid={finishMatchTestId}
          onClick={finishMatchReachable ? onFinishMatch : undefined}
          disabled={!finishMatchReachable || transitionDisabled}
          className="w-full inline-flex items-center justify-center gap-1.5 min-h-11 px-3 rounded-md border border-slate-500 text-slate-300 text-xs font-semibold uppercase tracking-wide bg-transparent transition-colors hover:bg-slate-700/40 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent"
        >
          {t("logger.ladder.finishMatch", "Terminar partido")}
        </button>
      </div>
    </div>
  );
}

export default MatchPeriodSelector;
